import type { ScoreField } from "@/types/template";
import { autoAdvance } from "@/components/filler/fields/fieldUtils";

interface Props {
  field: ScoreField;
  value: number | undefined;
  onChange: (value?: unknown) => void;
  compact?: boolean;
}

function ScoreLabel({ label, value }: Readonly<{ label: string; value: number | undefined }>) {
  return (
    <label>
      {label}: <strong>{value ?? "—"}</strong>
      <span className="field-required-marker"> *</span>
    </label>
  );
}

function CompactRadioScore({ field, onChange, value }: Readonly<Omit<Props, "compact">>) {
  const selected = field.scoreOptions?.find((o) => o.value === value);
  return (
    <>
      <ScoreLabel label={field.label} value={value} />
      <div className="score-radio-options">
        {selected && (
          <label
            className="score-radio-option selected"
            onClick={() => {
              onChange();
            }}
          >
            <input type="radio" name={field.id} checked readOnly />
            <span className="score-radio-value">({selected.value})</span>
            {selected.label}
          </label>
        )}
      </div>
    </>
  );
}

function RadioScore({ field, onChange, value }: Readonly<Omit<Props, "compact">>) {
  return (
    <>
      <ScoreLabel label={field.label} value={value} />
      <div className="score-radio-options">
        {field.scoreOptions?.map((opt) => (
          <label
            key={opt.value}
            className={`score-radio-option ${value === opt.value ? "selected" : ""}`}
          >
            <input
              type="radio"
              name={field.id}
              checked={value === opt.value}
              onChange={(e) => {
                onChange(opt.value);
                autoAdvance(e.currentTarget);
              }}
            />
            <span className="score-radio-value">({opt.value})</span>
            {opt.label}
          </label>
        ))}
      </div>
    </>
  );
}

function NumericScore({ field, onChange, value }: Readonly<Omit<Props, "compact">>) {
  return (
    <>
      <ScoreLabel label={field.label} value={value} />
      <div className="score-display">
        <input
          type="number"
          min={0}
          max={field.max}
          value={value ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") {
              onChange();
              return;
            }
            const num = Number.parseInt(v);
            if (!Number.isNaN(num)) {
              onChange(Math.max(0, Math.min(field.max, num)));
            }
          }}
        />
      </div>
    </>
  );
}

export function ScoreInput({ compact, field, onChange, value }: Readonly<Props>) {
  if (field.scoreOptions && field.scoreOptions.length > 0) {
    if (compact && value !== undefined) {
      return <CompactRadioScore field={field} value={value} onChange={onChange} />;
    }
    return <RadioScore field={field} value={value} onChange={onChange} />;
  }
  return <NumericScore field={field} value={value} onChange={onChange} />;
}
